/**
 * Foundation/Core — SidebarLinks
 *
 * Componente shell puro che renderizza una sidebar di navigazione a partire
 * da una lista di voci (`items`) di forma:
 *   { to, label, icon, section, primary?, title? }
 *
 * Raggruppa automaticamente per `section` (usando SECTION_LABELS del CORE)
 * e supporta un gruppo "advanced" collapsable in fondo.
 *
 * Nessuna dipendenza dal dominio: il chiamante fornisce gli `items` che
 * riflettono il Product Pack corrente (GI Presenze, GI Clienti, GI Studio, ...).
 */
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { SECTION_LABELS } from "./constants";

export function SidebarLinks({ items, onNavigate }) {
  const mainItems = items.filter((it) => it.section !== "advanced");
  const advancedItems = items.filter((it) => it.section === "advanced");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const grouped = [];
  let last = null;

  for (const it of mainItems) {
    if (last && it.section !== last) {
      grouped.push({ divider: true, key: `d-${it.section}` });
      const label = SECTION_LABELS[it.section];
      if (label) grouped.push({ sectionLabel: label, key: `l-${it.section}` });
    }
    grouped.push(it);
    last = it.section;
  }

  const renderNavItem = (it) => {
    const Icon = it.icon;
    return (
      <NavLink
        key={it.to}
        to={it.to}
        end={it.to === "/"}
        onClick={onNavigate}
        data-testid={`nav-${it.to.replace(/\//g, "") || "home"}`}
        className={({ isActive }) =>
          `group relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150
          ${isActive
            ? "bg-suite-primary/10 text-suite-primary shadow-[inset_0_0_0_1px_rgba(76,175,80,0.18)]"
            : "text-suite-neutral-700 hover:bg-suite-primary-light/60 hover:text-suite-primary"}`
        }
      >
        {({ isActive }) => (
          <>
            {isActive && <span aria-hidden="true" className="absolute inset-y-1.5 left-0 w-[3px] rounded-r-full bg-suite-primary" />}
            <Icon size={17} strokeWidth={(isActive || it.primary) ? 2.1 : 1.75} className="shrink-0" />
            <span className={`truncate ${it.primary ? "font-semibold" : ""}`} title={it.title || it.label}>{it.label}</span>
          </>
        )}
      </NavLink>
    );
  };

  return (
    <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5" data-testid="sidebar-nav">
      {grouped.map((it) => {
        if (it.divider) return <div key={it.key} className="my-2 border-t border-suite-neutral-200/70" />;
        if (it.sectionLabel) {
          return (
            <div key={it.key} className="px-3 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-suite-neutral-400">
              {it.sectionLabel}
            </div>
          );
        }
        return renderNavItem(it);
      })}
      {advancedItems.length > 0 && (
        <div className="mt-2 border-t border-suite-neutral-200/70 pt-2">
          <button
            type="button"
            onClick={() => setAdvancedOpen((value) => !value)}
            aria-expanded={advancedOpen}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-suite-neutral-400 hover:text-suite-neutral-700"
          >
            <ChevronDown size={12} className={`transition-transform ${advancedOpen ? "" : "-rotate-90"}`} />
            {SECTION_LABELS.advanced}
          </button>
          {advancedOpen && <div className="mt-1 space-y-0.5">{advancedItems.map(renderNavItem)}</div>}
        </div>
      )}
    </nav>
  );
}
